import { DropdownItem } from "@mcleod/components";
import { ValueDisplayValue } from "@mcleod/core";

/**
 * Utility class for Mobile Communication Control dropdown items
 */
export class MobileCommControlUtils {

    /**
     * Create dropdown items from an array of vendor names
     */
    private static createDropdownItems(vendors: string[]): DropdownItem[] {
        return vendors.map(vendor => new ValueDisplayValue(vendor, vendor))
            .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    }

    /**
     * Get the mobile communication vendor dropdown items
     */
    static getVendorDropdownItems(): DropdownItem[] {
        const vendors = [
            'CARRIERWEB',
            'DRVTECHWEB',
            'EROAD',
            'GEOTAB',
            'IGLOBAL',
            'JJKELLER',
            'KEEPTRUCKIN',
            'MAVENM',
            'MCLEODMOBL',
            'OMNITRACS1',
            'PEDIGREE',
            'PEGASUS',
            'PEOPLENET',
            'PLATFORMSC',
            'PNETELOGPR',
            'QCHOS',
            'QCPORTAL',
            'QCWORKFLOW',
            'RANDDC',
            'SAMSARA',
            'SYMPHONY',
            'TELOGIS',
            'TNDIRECTOR',
            'XRS',
            'ZONAR'
        ];

        return this.createDropdownItems(vendors);
    }

    /**
     * Get the trailer tracking vendor dropdown items
     */
    static getTrailerTrackingDropdownItems(): DropdownItem[] {
        const vendors = [
            'BLUETREE',
            'FLEETLOCAT',
            'IBRIGHT',
            'LBTECH',
            'LYTX',
            'ORBCOMM',
            'PARLMS',
            'SKYBITZ',
            'TLRSAMS',
            'VERIWISE',
            'VISTAR'
        ];

        return this.createDropdownItems(vendors);
    }

    /**
     * Get all vendor dropdown items (both mobile communication and trailer tracking)
     */
    static getAllVendorDropdownItems(): DropdownItem[] {
        const mobileCommVendors = this.getVendorDropdownItems();
        const trailerTrackingVendors = this.getTrailerTrackingDropdownItems();

        // Combine both arrays and return the combined result
        return [...mobileCommVendors, ...trailerTrackingVendors];
    }

    /**
     * Get the web URL for a given mobile communication vendor ID
     * @param vendorId The vendor ID to get the URL for
     * @returns The web URL for the vendor, or an empty string if not found
     */
    static getWebURL(vendorId: string): string {
        let url = "";

        try {
            if (!vendorId) {
                return url;
            }

            // Convert to uppercase to make comparisons case-insensitive
            const vendorPrefix = vendorId.toUpperCase();

            if (vendorPrefix.startsWith("OMNI")) {
                url = "https://apex-te-dev-ric.aws.roadnet.com/Account/Login";
            }
            if (vendorPrefix.startsWith("BLUET")) {
                url = "https://www.fleetmanager.com/#/login";
            }
            if (vendorPrefix.startsWith("CAD")) {
                url = "https://staging.cadec.com";
            }
            if (vendorPrefix.startsWith("CARR")) {
                url = "http://www.carrierweb.com";
            }
            if (vendorPrefix.startsWith("DRVTECHW")) {
                url = "http://connect.randmcnally.com/ConnectServicesPortal/Account/LogOn";
            }
            if (vendorPrefix.startsWith("EROAD")) {
                url = "https://depot.eroad.com/Portal/tracking/tracking";
            }
            if (vendorPrefix.startsWith("FLEETL")) {
                url = "http://fleet.spireon.com";
            }
            if (vendorPrefix.startsWith("FLEETC")) {
                url = "https://web.fleetcomplete.com";
            }
            if (vendorPrefix.startsWith("FTS")) {
                url = "https://www.ftsgps.com";
            }
            if (vendorPrefix.startsWith("GEOT")) {
                url = "https://my.geotab.com";
            }
            if (vendorPrefix.startsWith("IBR")) {
                url = "https://i360na.coretex.com";
            }
            if (vendorPrefix.startsWith("IGL")) {
                url = "https://alts.truckloadmdt.com/Account/LogOn?ReturnUrl=%2f";
            }
            if (vendorPrefix.startsWith("JJK")) {
                url = "https://www.kellerencompass.com/app/login/login.aspx";
            }
            if (vendorPrefix.startsWith("KEE")) {
                url = "https://keeptruckin.com/log-in";
            }
            if (vendorPrefix.startsWith("LBT")) {
                url = "https://www.lbtelematics.net";
            }
            if (vendorPrefix.startsWith("MCLEODMOBL")) {
                url = "http://www.mcleodsoftware.com";
            }
            if (vendorPrefix.startsWith("ORBC")) {
                url = "https://cargowatch.startrak.com/Par.LMS.CargoWatch.UI/Login.aspx?ReturnUrl=%2fPar.LMS.CargoWatch.UI%2fdefault.aspx";
            }
            if (vendorPrefix.startsWith("NAVI")) {
                url = "https://edlapidevlb.navistar.com/WebAdminStaging/";
            }
            if (vendorPrefix.startsWith("PED")) {
                url = "https://oneview2.pedigreetechnologies.com/lineage/login.action";
            }
            if (vendorPrefix.startsWith("PEG")) {
                url = "https://transflocommandcenter.com/CommandCenter/#/signin;redirectedUrl=%2F";
            }
            if (vendorPrefix.startsWith("PEO")) {
                url = "https://www.pfmlogin.com/pfm-main/authentication/login";
            }
            if (vendorPrefix.startsWith("PLA")) {
                url = "https://mcleod-demo.platformscience.com";
            }
            if (vendorPrefix.startsWith("PNET")) {
                url = "https://www.pfmlogin.com/pfm-main/authentication/login";
            }
            if (vendorPrefix.startsWith("QCP")) {
                url = "https://services.omnitracs.com/portalWeb/jsp/login.jsp?";
            }
            if (vendorPrefix.startsWith("QCT")) {
                url = "https://services.omnitracs.com/portalWeb/jsp/login.jsp";
            }
            if (vendorPrefix.startsWith("QCWORK")) {
                url = "https://services.omnitracs.com/portalWeb/jsp/login.jsp";
            }
            if (vendorPrefix.startsWith("QCHOS")) {
                url = "https://services.omnitracs.com/portalWeb/jsp/login.jsp";
            }
            if (vendorPrefix.startsWith("RANDD")) {
                url = "https://driverconnect.randmcnally.com/";
            }
            if (vendorPrefix.startsWith("SKY")) {
                url = "https://insight.skybitz.com";
            }
            if (vendorPrefix.startsWith("SAM") || vendorPrefix.startsWith("TLRSAMS")) {
                url = "https://cloud.samsara.com";
            }
            if (vendorPrefix.startsWith("TND")) {
                url = "https://director-us.teletracnavman.net/avl3";
            }
            if (vendorPrefix.startsWith("TELO")) {
                url = "https://login-la-fleet.platform.telogis.com/#";
            }
            if (vendorPrefix.startsWith("THERM")) {
                url = "https://www.tktracking.com";
            }
            if (vendorPrefix.startsWith("TRACKP")) {
                url = "http://www.trackpointsystems.com";
            }
            if (vendorPrefix.startsWith("VERI")) {
                url = "https://www.fleetview.net/VeriwiseWeb/ai/Home";
            }
            if (vendorPrefix.startsWith("WEBTECH")) {
                url = "https://192.40.237.119/wtw/jsp/QSecurity/login.jsp";
            }
            if (vendorPrefix.startsWith("XRS")) {
                url = "https://xataxrs.com";
            }
            if (vendorPrefix.startsWith("ZON")) {
                url = "https://zonarsystems.net";
            }
            if (vendorPrefix.startsWith("MAV")) {
                url = "https://accounts-staging.mavenmachines.com/?action=login";
            }

            return url;
        } catch (error) {
            console.error("Error getting web URL:", error);
            return url;
        }
    }
}
